import React, { useState, useEffect, useMemo } from "react";
import { FaCheck, FaClock } from "react-icons/fa";

/* ================= TYPES ================= */

interface Property {
  id: string;
  upi: string;
  valuationStep?: {
    step?: number;
    timeRemaining?: string;
  };
}

/* ================= STEPS ================= */

const STEPS = [
  { id: 1, name: "Submit Property" },
  { id: 2, name: "Payment" },
  { id: 3, name: "Data Collection" },
  { id: 4, name: "Professional Opinion" },
  { id: 5, name: "Preview Early Valuation" },
  { id: 6, name: "Edit Final Report" },
  { id: 7, name: "Valuation Completed" },
];

/* ================= HELPERS ================= */

function getRemainingTime(timeRemaining?: string): string {
  if (!timeRemaining) return "";

  const now = new Date();
  const endTime = new Date(timeRemaining);
  const diffMs = endTime.getTime() - now.getTime();

  if (diffMs <= 0) return "Overdue";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}min left`;
  return `${minutes} min left`;
}

/* ================= COMPONENT ================= */

const PropertyValuationTracker: React.FC<{
  property: Property;
}> = ({ property }) => {
  const [currentStep, setCurrentStep] = useState(
    property.valuationStep?.step || 0,
  );
  const [timeAmount, setTimeAmount] = useState("");
  const [timeUnit, setTimeUnit] = useState("days");
  const [savedTimeRemaining, setSavedTimeRemaining] = useState(
    property.valuationStep?.timeRemaining,
  );
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string | undefined>(
    undefined,
  );
  const [validationError, setValidationError] = useState("");
  const [now, setNow] = useState(new Date());
  console.log("property in tracker:", property);
  // Update clock every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setCurrentStep(property.valuationStep?.step || 0);
    setTimeRemaining(property.valuationStep?.timeRemaining);
  }, [property]);

  const updateProperty = async (step: number, time: string) => {
    setLoading(true);
    setValidationError("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      const BASE_URL =
        import.meta.env.VITE_API_BASE_URL ||
        "https://valuate-api.vercel.app/api/v1";

      const response = await fetch(`${BASE_URL}/properties/${property.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            step: step,
            timeRemaining: time,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update");
      }

      const data = await response.json();
      setCurrentStep(data.data?.valuationStep?.step || step);
      setTimeRemaining(data.data?.valuationStep?.timeRemaining || time);

      // Clear inputs after successful update
      setTimeAmount("");
    } catch (error: any) {
      console.error("Error updating property:", error);
      setValidationError(error.message || "Failed to update property");
    } finally {
      setLoading(false);
    }
  };
  console.log();

  const handleStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStep = parseInt(e.target.value);

    if (newStep < 0 || newStep > STEPS.length) {
      setValidationError("Invalid step selected");
      return;
    }

    setValidationError("");
    setCurrentStep(newStep);
    updateProperty(newStep, savedTimeRemaining || "");
  };

  const handleTimeSubmit = () => {
    const amount = parseInt(timeAmount);

    // Validation
    if (!timeAmount || timeAmount.trim() === "") {
      setValidationError("Please enter a time amount");
      return;
    }

    if (isNaN(amount) || amount <= 0) {
      setValidationError("Please enter a valid positive number");
      return;
    }

    if (amount > 365 && timeUnit === "days") {
      setValidationError("Maximum 365 days allowed");
      return;
    }

    if (amount > 8760 && timeUnit === "hours") {
      setValidationError("Maximum 8760 hours (1 year) allowed");
      return;
    }

    if (amount > 525600 && timeUnit === "minutes") {
      setValidationError("Maximum 525600 minutes (1 year) allowed");
      return;
    }

    setValidationError("");

    // Calculate future date based on amount and unit
    const now = new Date();
    let futureDate = new Date();

    if (timeUnit === "minutes") {
      futureDate = new Date(now.getTime() + amount * 60 * 1000);
    } else if (timeUnit === "hours") {
      futureDate = new Date(now.getTime() + amount * 60 * 60 * 1000);
    } else if (timeUnit === "days") {
      futureDate = new Date(now.getTime() + amount * 24 * 60 * 60 * 1000);
    }

    const formattedDate = futureDate.toISOString();
    setSavedTimeRemaining(formattedDate);
    updateProperty(currentStep, formattedDate);
  };

  const steps: any = useMemo(() => {
    return STEPS.map((s) => ({
      ...s,
      completed: s.id < currentStep,
      inProgress: s.id === currentStep,
    }));
  }, [currentStep]);

  const completedCount = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const hasActiveStep = steps.some((s) => s.inProgress);

  // const remaining = hasActiveStep ? getRemainingTime(timeRemaining) : "";
  const remaining = hasActiveStep ? getRemainingTime(timeRemaining) : "";

  // Progress: 100% when user reaches the last step
  const isOnLastStep = currentStep === totalSteps;
  const progressbar =
    totalSteps === 0
      ? 0
      : isOnLastStep
        ? 100
        : Math.round(
            ((completedCount + (hasActiveStep ? 0.5 : 0)) / totalSteps) * 100,
          );

  const remainingbar = hasActiveStep ? getRemainingTime(timeRemaining) : "";

  function getRemainingTime(timeRemaining?: string): string {
    if (!timeRemaining) return "";

    const now = new Date();
    const endTime = new Date(timeRemaining);
    const diffMs = endTime.getTime() - now.getTime();

    if (diffMs <= 0) return "Done";

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}min left`;
    return `${minutes} min left`;
  }

  return (
    <div className="">
      <div className="">
        {/* Header Controls */}
        {/* <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4"> */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Step
            </label>
            <select
              value={currentStep}
              onChange={handleStepChange}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value={0}>Not Started</option>
              {STEPS.map((step) => (
                <option key={step.id} value={step.id}>
                  Step {step.id}: {step.name}
                </option>
              ))}
            </select>
          </div> */}

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Remaining
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                max="999"
                placeholder="0"
                value={timeAmount}
                onChange={(e) => {
                  setTimeAmount(e.target.value);
                  setValidationError("");
                }}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
              <select
                value={timeUnit}
                onChange={(e) => {
                  setTimeUnit(e.target.value);
                  setValidationError("");
                }}
                disabled={loading}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
              <button
                onClick={handleTimeSubmit}
                disabled={loading || !timeAmount}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                Set
              </button>
            </div>
          </div> */}
        {/* </div> */}

        {/* Validation Error */}
        {validationError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        )}

        {/* Timeline */}
        <div className="relative">
          {/* Background track */}
          <div className="absolute top-[20px] left-10 right-10 h-1 bg-gray-200 rounded-full" />

          {/* Progress fill - Blue up to and including current step */}
          <div
            className="absolute top-[20px] left-0 h-1 bg-blue-600 rounded-full transition-all duration-700 ease-out"
            style={{
              width:
                currentStep > 0
                  ? `${Math.round((currentStep / totalSteps) * 100 - 5)}%`
                  : "0%",
            }}
          />
          {/* Steps */}
          <div className="relative flex items-start justify-between w-full">
            {STEPS.map((step, idx) => {
              const isLast = idx === STEPS.length - 1;
              const isDone = step.id < currentStep;
              const isActive = step.id === currentStep;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center relative z-10">
                    <div
                      className={`
                                          w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold shadow-md
                                          transition-all duration-300
                                          ${
                                            isDone
                                              ? "bg-green-600"
                                              : isActive
                                                ? "bg-blue-600 ring-4 ring-blue-100"
                                                : "bg-gray-300 text-gray-600"
                                          }`}
                    >
                      {isDone ? <FaCheck /> : idx + 1}
                    </div>

                    {/* <div className="mt-3 top-8 absolute text-xs font-medium text-gray-800 whitespace-nowrap">
                      {step.name}
                    </div> */}

                    {isActive && remaining && (
                      <div className="mt-2 text-xs text-blue-700 font-medium flex items-center gap-1.5">
                        <FaClock size={13} />
                        {remainingbar}
                      </div>
                    )}
                  </div>

                  {!isLast && <div className="flex-1 bg-transparent" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Overall Progress Percentage */}
        <div className="mt-2 text-center">
          <div className="text-2xl font-bold text-blue-600 mb-1">
            {progressbar}%
          </div>
          <div className="text-sm text-gray-600">Overall Progress</div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="mt-4 text-center text-sm text-gray-500">
            Updating...
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyValuationTracker;
